import { 
  getFirestore, 
  collection, 
  doc, 
  setDoc, 
  getDocs, 
  query, 
  where, 
  serverTimestamp 
} from 'firebase/firestore';
import { getAuth, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';

/**
 * 初始化Firestore数据库
 * 创建必要的集合和文档
 */
export const initializeFirestore = async () => {
  const db = getFirestore();
  const auth = getAuth();
  
  console.log('开始初始化Firestore数据库...');
  
  try {
    let adminResult = null;
    
    try {
      // 创建初始分类
      await initializeCategories(db);
      console.log('分类初始化完成');
    } catch (e) {
      console.error('分类初始化失败:', e);
    }
    
    try {
      // 创建初始产品
      await initializeProducts(db);
      console.log('产品初始化完成');
    } catch (e) {
      console.error('产品初始化失败:', e);
    }
    
    try {
      // 创建初始命理师
      await initializeFortuneTellers(db);
      console.log('命理师初始化完成');
    } catch (e) {
      console.error('命理师初始化失败:', e);
    }
    
    try {
      // 创建评价集合
      await initializeComments(db);
      console.log('评价集合初始化完成');
    } catch (e) {
      console.error('评价集合初始化失败:', e);
    }
    
    try {
      // 创建管理员账户
      adminResult = await createAdminUser(auth, db);
      console.log('管理员账户初始化完成');
    } catch (e) {
      console.error('管理员账户初始化失败:', e);
    }
    
    try {
      // 创建系统设置
      await initializeSettings(db);
      console.log('系统设置初始化完成');
    } catch (e) {
      console.error('系统设置初始化失败:', e);
    }
    
    console.log('Firestore数据库初始化完成!');
    return {
      success: true,
      message: 'Firestore数据库初始化完成',
      adminEmail: adminResult?.adminEmail,
      adminPassword: adminResult?.adminPassword
    };
  } catch (error) {
    console.error('Firestore数据库初始化失败:', error);
    return {
      success: false,
      message: '初始化失败: ' + error.message,
      error
    };
  }
};

/**
 * 创建产品分类
 */
const initializeCategories = async (db) => {
  const categoriesRef = collection(db, 'categories');
  
  // 检查集合是否已存在数据
  const querySnapshot = await getDocs(categoriesRef);
  if (!querySnapshot.empty) {
    console.log('分类集合已存在，跳过初始化');
    return;
  }
  
  console.log('初始化五行分类...');
  
  // 创建五行分类文档
  const categories = [
    {
      id: 'wood',
      name: '木',
      description: '木主生长，对应春季，代表生命、健康与成长',
      icon: '🌱',
      order: 1,
      element: 'wood',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    },
    {
      id: 'fire',
      name: '火',
      description: '火主热情，对应夏季，代表热情、爱情与活力',
      icon: '🔥',
      order: 2,
      element: 'fire',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    },
    {
      id: 'earth',
      name: '土',
      description: '土主稳固，对应季末，代表稳定、滋养与丰收',
      icon: '🪨',
      order: 3,
      element: 'earth',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    },
    {
      id: 'metal',
      name: '金',
      description: '金主收敛，对应秋季，代表毅力、勇气与荣耀',
      icon: '⚱️',
      order: 4,
      element: 'metal',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    },
    {
      id: 'water',
      name: '水',
      description: '水主智慧，对应冬季，代表智慧、直觉与潜藏力量',
      icon: '💧',
      order: 5,
      element: 'water',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }
  ];
  
  // 添加分类到数据库
  for (const category of categories) {
    const categoryId = category.id;
    await setDoc(doc(db, 'categories', categoryId), category);
  }
  
  console.log(`成功添加 ${categories.length} 个五行分类`);
};

/**
 * 创建初始产品
 */
const initializeProducts = async (db) => {
  const productsRef = collection(db, 'products');
  
  // 检查集合是否已存在数据
  const querySnapshot = await getDocs(productsRef);
  if (!querySnapshot.empty) {
    console.log('产品集合已存在，跳过初始化');
    return;
  }
  
  console.log('初始化产品...');
  
  // 创建产品文档 - 移除properties字段，增加features特点和reviews评价
  const products = [
    {
      name: '紫水晶能量手链',
      description: '紫水晶具有平静心灵、净化能量的功效，帮助缓解压力和焦虑，增强直觉和灵性意识。',
      price: 299,
      discountPrice: 259,
      stock: 100,
      images: [
        'https://firebasestorage.googleapis.com/v0/b/destinycrystal-ff557.appspot.com/o/products%2Famethyst_bracelet.jpg?alt=media'
      ],
      imageUrl: 'https://firebasestorage.googleapis.com/v0/b/destinycrystal-ff557.appspot.com/o/products%2Famethyst_bracelet.jpg?alt=media',
      categoryId: 'water',
      status: 'active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // 移除properties字段
      tags: ['水晶', '手链', '紫水晶', '平静', '灵性'],
      // 添加产品特点
      features: [
        '紫水晶能平静心灵，净化负面能量',
        '增强直觉和灵性意识',
        '帮助缓解压力和焦虑',
        '提升冥想和灵性修习的效果',
        '精心挑选的优质紫水晶珠子，手工制作'
      ],
      // 添加产品评价
      rating: 5.0,
      reviewCount: 18,
      reviews: [
        {
          id: 'review1',
          userId: 'user1',
          userName: '王晓莉',
          rating: 5,
          content: '收到手链后非常惊喜，紫水晶的颜色很漂亮，质感也很好。戴上后感觉整个人都平静了很多，晚上睡眠也更好了。',
          date: new Date('2023-11-10').toISOString(),
          avatar: 'https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/avatar-default.jpg'
        },
        {
          id: 'review2',
          userId: 'user2',
          userName: '李明',
          rating: 5,
          content: '这条手链能量很强，收到第一天就能感受到水晶的能量。质量也很好，很值这个价格。',
          date: new Date('2023-12-05').toISOString(),
          avatar: 'https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/avatar-default.jpg'
        }
      ]
    },
    {
      name: '清透白水晶手链',
      description: '白水晶是万能的能量放大器，有助于增强其他水晶的能量，同时净化周围环境。',
      price: 259,
      discountPrice: 0,
      stock: 85,
      images: [
        'https://firebasestorage.googleapis.com/v0/b/destinycrystal-ff557.appspot.com/o/products%2Fclear_quartz_bracelet.jpg?alt=media'
      ],
      imageUrl: 'https://firebasestorage.googleapis.com/v0/b/destinycrystal-ff557.appspot.com/o/products%2Fclear_quartz_bracelet.jpg?alt=media',
      categoryId: 'metal',
      status: 'active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // 移除properties字段
      tags: ['水晶', '手链', '白水晶', '能量', '净化'],
      features: [
        '白水晶是能量放大器，可增强其他水晶效果',
        '净化周围环境和个人能量场',
        '增强精神力量和专注力',
        '高透明度优质白水晶',
        '适合日常佩戴和冥想使用'
      ],
      rating: 5.0,
      reviewCount: 12,
      reviews: [
        {
          id: 'review3',
          userId: 'user3',
          userName: '张伟',
          rating: 5,
          content: '白水晶的能量很纯净，戴上后感觉思路清晰了很多，工作效率提高了。做冥想的时候也很有帮助。',
          date: new Date('2023-10-22').toISOString(),
          avatar: 'https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/avatar-default.jpg'
        },
        {
          id: 'review4',
          userId: 'user4',
          userName: '刘芳',
          rating: 5,
          content: '水晶质量很好，通透无瑕疵。我把它和其他水晶一起佩戴，感觉效果更好了。客服推荐的很棒！',
          date: new Date('2023-11-18').toISOString(),
          avatar: 'https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/avatar-default.jpg'
        }
      ]
    },
    {
      name: '玫瑰粉晶爱情吊坠',
      description: '粉晶是爱情之石，可以帮助打开心轮，吸引爱情，增强自爱和人际关系。',
      price: 329,
      discountPrice: 299,
      stock: 50,
      images: [
        'https://firebasestorage.googleapis.com/v0/b/destinycrystal-ff557.appspot.com/o/products%2Frose_quartz_pendant.jpg?alt=media'
      ],
      imageUrl: 'https://firebasestorage.googleapis.com/v0/b/destinycrystal-ff557.appspot.com/o/products%2Frose_quartz_pendant.jpg?alt=media',
      categoryId: 'fire',
      status: 'active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // 移除properties字段
      tags: ['项链', '吊坠', '粉晶', '爱情', '心轮'],
      features: [
        '粉晶有助于打开心轮，吸引爱情',
        '增强自爱和提升人际关系',
        '缓解心灵创伤，促进情感疗愈',
        '优雅的设计，适合日常佩戴',
        '采用925银制作吊坠底座，搭配高品质粉晶'
      ],
      rating: 5.0,
      reviewCount: 15,
      reviews: [
        {
          id: 'review5',
          userId: 'user5',
          userName: '陈小爱',
          rating: 5,
          content: '收到这个吊坠戴上后两周，真的有好几个异性来搭讪，而且感觉自己的心情也变好了很多，更容易感受到美好的事物。',
          date: new Date('2023-09-30').toISOString(),
          avatar: 'https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/avatar-default.jpg'
        },
        {
          id: 'review6',
          userId: 'user6',
          userName: '何静',
          rating: 5,
          content: '粉晶的颜色很温柔，吊坠的设计也很精致。感觉自己戴上后更有女人味了，朋友都说我变得更柔和了。',
          date: new Date('2023-10-15').toISOString(),
          avatar: 'https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/avatar-default.jpg'
        }
      ]
    },
    {
      name: '黑曜石保护掌上石',
      description: '黑曜石是强大的保护石，能够阻挡负面能量，增强接地感，提供情感稳定。',
      price: 199,
      discountPrice: 0,
      stock: 65,
      images: [
        'https://firebasestorage.googleapis.com/v0/b/destinycrystal-ff557.appspot.com/o/products%2Fobsidian_palm_stone.jpg?alt=media'
      ],
      imageUrl: 'https://firebasestorage.googleapis.com/v0/b/destinycrystal-ff557.appspot.com/o/products%2Fobsidian_palm_stone.jpg?alt=media',
      categoryId: 'earth',
      status: 'active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // 移除properties字段
      tags: ['掌上石', '黑曜石', '保护', '接地', '稳定'],
      features: [
        '黑曜石能阻挡负面能量，提供强大保护',
        '增强接地感，让您更稳定踏实',
        '缓解压力，带来情感稳定',
        '精心打磨的掌上石，手感顺滑',
        '冥想或日常随身携带均可使用'
      ],
      rating: 5.0,
      reviewCount: 9,
      reviews: [
        {
          id: 'review7',
          userId: 'user7',
          userName: '吴强',
          rating: 5,
          content: '这块黑曜石掌上石质感很好，拿在手里很有分量。工作压力大的时候拿出来摩挲几下，感觉压力就减轻了很多。',
          date: new Date('2023-11-05').toISOString(),
          avatar: 'https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/avatar-default.jpg'
        },
        {
          id: 'review8',
          userId: 'user8',
          userName: '郑小雨',
          rating: 5,
          content: '黑曜石的能量很强，放在办公桌上感觉整个工作环境都变得平静了，负面情绪少了很多。打磨得很光滑，质量很好。',
          date: new Date('2023-12-01').toISOString(),
          avatar: 'https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/avatar-default.jpg'
        }
      ]
    },
    {
      name: '七脉轮平衡水晶套装',
      description: '七种不同的水晶对应人体七个脉轮，帮助平衡身心能量，促进整体健康。',
      price: 699,
      discountPrice: 599,
      stock: 30,
      images: [
        'https://firebasestorage.googleapis.com/v0/b/destinycrystal-ff557.appspot.com/o/products%2Fchakra_set.jpg?alt=media'
      ],
      imageUrl: 'https://firebasestorage.googleapis.com/v0/b/destinycrystal-ff557.appspot.com/o/products%2Fchakra_set.jpg?alt=media',
      categoryId: 'wood',
      status: 'active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // 移除properties字段
      tags: ['套装', '脉轮', '平衡', '健康', '冥想'],
      features: [
        '七种精选水晶对应七个脉轮',
        '帮助平衡身心能量，促进整体健康',
        '适合冥想、瑜伽和能量工作',
        '每块水晶都经过净化和能量加持',
        '配有精美收纳盒和使用指南'
      ],
      rating: 5.0,
      reviewCount: 21,
      reviews: [
        {
          id: 'review9',
          userId: 'user9',
          userName: '杨光',
          rating: 5,
          content: '套装中每块水晶的能量都很纯净，冥想时摆在身边各个脉轮的位置，能感受到能量在体内流动。包装也很精美，很适合送人。',
          date: new Date('2023-08-15').toISOString(),
          avatar: 'https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/avatar-default.jpg'
        },
        {
          id: 'review10',
          userId: 'user10',
          userName: '林雨',
          rating: 5,
          content: '作为瑜伽教练，我很推荐这套脉轮水晶。质量非常好，能量也很强。附赠的说明书对了解各个水晶的功效很有帮助。',
          date: new Date('2023-09-20').toISOString(),
          avatar: 'https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/avatar-default.jpg'
        }
      ]
    }
  ];
  
  // 添加产品到数据库
  for (const product of products) {
    await setDoc(doc(productsRef), product);
  }
  
  console.log(`成功添加 ${products.length} 个产品`);
};

/**
 * 创建初始命理师
 */
const initializeFortuneTellers = async (db) => {
  const fortuneTellersRef = collection(db, 'fortuneTellers');
  
  // 检查集合是否已存在数据
  const querySnapshot = await getDocs(fortuneTellersRef);
  if (!querySnapshot.empty) {
    console.log('命理师集合已存在，跳过初始化');
    return;
  }
  
  console.log('初始化命理师...');
  
  // 创建命理师文档
  const fortuneTellers = [
    {
      name: '陈大师',
      title: '资深命理大师',
      photoURL: 'https://firebasestorage.googleapis.com/v0/b/destinycrystal-ff557.appspot.com/o/fortunetellers%2Fmaster_chen.jpg?alt=media',
      rating: 4.9,
      reviewCount: 156,
      price: 399,
      specialties: ['八字命盘', '紫微斗数', '塔罗牌', '事业运势'],
      featured: true,
      experience: 15,
      bio: '陈大师拥有15年命理咨询经验，精通传统八字命盘分析和紫微斗数，并融合现代心理学知识，为客户提供全面而精准的命理解读和人生指导。',
      status: 'active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      availability: {
        monday: ['10:00', '14:00', '16:00', '20:00'],
        tuesday: ['10:00', '14:00', '16:00', '20:00'],
        wednesday: ['14:00', '16:00', '20:00'],
        thursday: ['10:00', '14:00', '16:00'],
        friday: ['10:00', '14:00', '16:00', '20:00'],
        saturday: ['10:00', '14:00', '16:00', '20:00'],
        sunday: ['14:00', '16:00', '20:00']
      }
    },
    {
      name: '林老师',
      title: '紫微斗数专家',
      photoURL: 'https://firebasestorage.googleapis.com/v0/b/destinycrystal-ff557.appspot.com/o/fortunetellers%2Fteacher_lin.jpg?alt=media',
      rating: 4.7,
      reviewCount: 98,
      price: 349,
      specialties: ['紫微斗数', '风水', '姓名学', '感情运势'],
      featured: false,
      experience: 10,
      bio: '林老师专注于紫微斗数研究十余年，尤其擅长解读感情和婚姻运势，帮助众多客户理清感情困惑，找到适合自己的感情发展方向。',
      status: 'active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      availability: {
        monday: ['10:00', '14:00', '16:00'],
        tuesday: ['10:00', '14:00', '16:00', '20:00'],
        wednesday: ['10:00', '14:00', '16:00', '20:00'],
        thursday: ['14:00', '16:00', '20:00'],
        friday: ['10:00', '14:00', '16:00'],
        saturday: ['10:00', '14:00', '16:00', '20:00'],
        sunday: ['14:00', '16:00']
      }
    },
    {
      name: '张玉华',
      title: '水晶能量大师',
      photoURL: 'https://firebasestorage.googleapis.com/v0/b/destinycrystal-ff557.appspot.com/o/fortunetellers%2Fmaster_zhang.jpg?alt=media',
      rating: 4.8,
      reviewCount: 120,
      price: 379,
      specialties: ['水晶能量', '灵气疗愈', '脉轮平衡', '能量场净化'],
      featured: true,
      experience: 12,
      bio: '张玉华老师精通各类水晶能量和属性，能够根据个人命理和能量场状况，推荐最适合的水晶组合，帮助调整能量场和提升运势。',
      status: 'active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      availability: {
        monday: ['10:00', '14:00', '20:00'],
        tuesday: ['10:00', '14:00', '16:00'],
        wednesday: ['10:00', '14:00', '16:00', '20:00'],
        thursday: ['14:00', '16:00'],
        friday: ['10:00', '14:00', '16:00', '20:00'],
        saturday: ['10:00', '14:00', '16:00', '20:00'],
        sunday: ['16:00', '20:00']
      }
    }
  ];
  
  // 添加命理师到数据库
  for (const fortuneTeller of fortuneTellers) {
    await setDoc(doc(fortuneTellersRef), fortuneTeller);
  }
  
  console.log(`成功添加 ${fortuneTellers.length} 个命理师`);
};

/**
 * 创建管理员用户
 */
const createAdminUser = async (auth, db) => {
  console.log('检查管理员用户...');
  
  // 检查是否已有管理员用户
  const usersRef = collection(db, 'users');
  const adminQuery = query(usersRef, where('role', '==', 'admin'));
  const adminSnapshot = await getDocs(adminQuery);
  
  if (!adminSnapshot.empty) {
    console.log('管理员用户已存在，跳过创建');
    return;
  }
  
  // 创建管理员账户的邮箱和密码
  const adminEmail = 'admin@destinycrystal.com';
  const adminPassword = 'Admin@123456'; // 生产环境中应使用更强密码并安全传递
  const adminDisplayName = '系统管理员';
  
  try {
    // 创建新管理员用户
    const userCredential = await createUserWithEmailAndPassword(auth, adminEmail, adminPassword);
    const user = userCredential.user;
    
    // 更新用户资料
    await updateProfile(user, { displayName: adminDisplayName });
    
    // 创建用户文档
    await setDoc(doc(db, 'users', user.uid), {
      email: adminEmail,
      displayName: adminDisplayName,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      role: 'admin', // 设置管理员角色
      lastLogin: serverTimestamp()
    });
    
    console.log(`成功创建管理员用户: ${adminEmail}`);
    console.log('重要提示: 请保存管理员密码，并在首次登录后修改密码');
    return {
      success: true,
      adminEmail,
      adminPassword
    };
  } catch (error) {
    // 如果用户已存在，但role不是admin
    if (error.code === 'auth/email-already-in-use') {
      console.log(`用户 ${adminEmail} 已存在，检查是否为管理员...`);
      
      // 查询用户文档
      const userQuery = query(usersRef, where('email', '==', adminEmail));
      const userSnapshot = await getDocs(userQuery);
      
      if (!userSnapshot.empty) {
        const userDoc = userSnapshot.docs[0];
        const userData = userDoc.data();
        
        if (userData.role !== 'admin') {
          // 更新为管理员角色
          await setDoc(doc(db, 'users', userDoc.id), {
            ...userData,
            role: 'admin',
            updatedAt: serverTimestamp()
          });
          console.log(`已将用户 ${adminEmail} 更新为管理员角色`);
        }
      }
    } else {
      console.error('创建管理员用户失败:', error);
      throw error;
    }
  }
};

/**
 * 创建系统设置
 */
const initializeSettings = async (db) => {
  const settingsRef = collection(db, 'settings');
  
  // 检查集合是否已存在数据
  const querySnapshot = await getDocs(settingsRef);
  if (!querySnapshot.empty) {
    console.log('设置集合已存在，跳过初始化');
    return;
  }
  
  console.log('初始化系统设置...');
  
  // 创建设置文档
  const settings = [
    {
      id: 'site-info',
      name: '网站信息',
      values: {
        siteName: 'DestinyCrystal',
        siteDescription: '专业水晶命理品牌，提供高品质水晶饰品和专业命理服务',
        contactEmail: 'contact@destinycrystal.com',
        contactPhone: '+86 1388 8888 8888',
        address: '上海市浦东新区张江高科技园区'
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    },
    {
      id: 'shipping',
      name: '配送设置',
      values: {
        freeShippingThreshold: 299, // 满299元免运费
        baseShippingFee: 15, // 基础运费15元
        expressShippingFee: 30, // 快递运费30元
        internationalShipping: false // 是否支持国际配送
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    },
    {
      id: 'payment',
      name: '支付设置',
      values: {
        acceptWechatPay: true,
        acceptAliPay: true,
        acceptUnionPay: true,
        acceptCreditCards: false
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }
  ];
  
  // 添加设置到数据库
  for (const setting of settings) {
    const settingId = setting.id;
    delete setting.id;
    await setDoc(doc(db, 'settings', settingId), setting);
  }
  
  console.log(`成功添加 ${settings.length} 个系统设置`);
};

/**
 * 创建评价集合
 */
const initializeComments = async (db) => {
  const commentsRef = collection(db, 'comments');
  
  // 检查集合是否已存在数据
  const querySnapshot = await getDocs(commentsRef);
  if (!querySnapshot.empty) {
    console.log('评价集合已存在，跳过初始化');
    return;
  }
  
  console.log('初始化评价集合...');
  
  // 创建产品评价示例
  const productComments = [
    {
      userId: 'user1',
      userName: '王晓莉',
      content: '收到手链后非常惊喜，紫水晶的颜色很漂亮，质感也很好。戴上后感觉整个人都平静了很多，晚上睡眠也更好了。',
      rating: 5,
      targetId: '', // 这里会在循环中填入实际产品ID
      targetType: 'product',
      status: 'active',
      createdAt: serverTimestamp(),
      hasReply: false,
      replies: []
    },
    {
      userId: 'user2',
      userName: '李明',
      content: '这条手链能量很强，收到第一天就能感受到水晶的能量。质量也很好，很值这个价格。',
      rating: 5,
      targetId: '', // 这里会在循环中填入实际产品ID
      targetType: 'product',
      status: 'active',
      createdAt: serverTimestamp(),
      hasReply: false,
      replies: []
    }
  ];
  
  // 创建命理师评价示例
  const fortuneTellerComments = [
    {
      userId: 'user3',
      userName: '张伟',
      content: '陈大师非常专业，分析得很准确。他分析了我的八字后，对我的事业发展给出了很有针对性的建议，现在按照他的指导在做调整，感觉方向更清晰了。',
      rating: 5,
      targetId: '', // 这里会在循环中填入实际命理师ID
      targetType: 'fortuneTeller',
      status: 'active',
      createdAt: serverTimestamp(),
      hasReply: true,
      replies: [
        {
          content: '感谢您的正面评价！很高兴能够帮到您，期待您事业蒸蒸日上！',
          adminId: 'admin',
          createdAt: serverTimestamp()
        }
      ]
    },
    {
      userId: 'user4',
      userName: '刘芳',
      content: '林老师的紫微斗数解读非常精准，特别是对我感情问题的分析，直接点出了问题所在。咨询后我调整了自己的心态，现在感情生活顺利多了。',
      rating: 5,
      targetId: '', // 这里会在循环中填入实际命理师ID
      targetType: 'fortuneTeller',
      status: 'active',
      createdAt: serverTimestamp(),
      hasReply: false,
      replies: []
    }
  ];
  
  // 获取所有产品和命理师的ID
  const productsSnapshot = await getDocs(collection(db, 'products'));
  const fortuneTellersSnapshot = await getDocs(collection(db, 'fortuneTellers'));
  
  // 添加产品评价
  if (!productsSnapshot.empty) {
    const productIds = productsSnapshot.docs.map(doc => doc.id);
    
    for (let i = 0; i < productComments.length; i++) {
      // 随机选择一个产品ID
      const randomProductId = productIds[Math.floor(Math.random() * productIds.length)];
      const comment = { ...productComments[i], targetId: randomProductId };
      
      // 添加评价
      await setDoc(doc(commentsRef), comment);
    }
  }
  
  // 添加命理师评价
  if (!fortuneTellersSnapshot.empty) {
    const fortuneTellerIds = fortuneTellersSnapshot.docs.map(doc => doc.id);
    
    for (let i = 0; i < fortuneTellerComments.length; i++) {
      // 随机选择一个命理师ID
      const randomFortuneTellerId = fortuneTellerIds[Math.floor(Math.random() * fortuneTellerIds.length)];
      const comment = { ...fortuneTellerComments[i], targetId: randomFortuneTellerId };
      
      // 添加评价
      await setDoc(doc(commentsRef), comment);
    }
  }
  
  console.log(`成功添加 ${productComments.length + fortuneTellerComments.length} 个评价示例`);
};

export default initializeFirestore;